import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { ApplicationClass, ConstraintCheckResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner } from '../../ui/components';

const LAYER_LABELS: Record<string, string> = {
  ZONING: 'Zoning',
  FLOOD: 'Flood risk',
  COASTAL_SETBACK: 'Coastal setback',
  HERITAGE: 'Heritage',
  AVIATION_HEIGHT: 'Aviation height (SCAA)',
};

/**
 * The spec's own "huge goodwill feature": free, instant parcel -> zoning /
 * constraint report. Calls GET /api/v1/m17/constraint-check?parcelNo=... —
 * the exact same buildConstraintReport() helper the real application
 * /validate step runs automatically (see m17-planning/src/routes/constraint.ts).
 * Also surfaces the published ApplicationClass reference config (checklist,
 * fee formula, SLA) from GET /api/v1/m17/application-classes.
 */
export function ConstraintChecker() {
  const [parcelNo, setParcelNo] = useState('');
  const [result, setResult] = useState<ConstraintCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const classesQ = useApiQuery<{ items: ApplicationClass[] }>('/api/v1/m17/application-classes');
  const [expandedClass, setExpandedClass] = useState<string | null>(null);

  const check = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parcelNo.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.get<ConstraintCheckResponse>(`/api/v1/m17/constraint-check?parcelNo=${encodeURIComponent(parcelNo.trim())}`);
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Constraint check failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Constraint Checker"
        subtitle="Enter a C9 GeoServe parcel number for an instant zoning / constraint report — the same check the application validation step runs automatically."
      />

      <form onSubmit={check} className="card mb-6 flex flex-wrap items-end gap-3 p-5">
        <Field label="Parcel number" className="min-w-[220px] flex-1">
          <input
            className="field-input"
            placeholder="e.g. PS/1234/2026"
            value={parcelNo}
            onChange={(e) => setParcelNo(e.target.value)}
          />
        </Field>
        <button type="submit" className="btn-primary" disabled={loading || !parcelNo.trim()}>
          {loading ? 'Checking…' : 'Check parcel'}
        </button>
      </form>

      {error ? (
        <div className="mb-6">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {loading ? <Spinner label="Running spatial intersect…" /> : null}

      {result ? (
        <div className="mb-8 card p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-800">{result.parcelNo}</div>
              <div className="text-xs text-slate-500">
                {result.district} · {result.tenureType.replaceAll('_', ' ')} · {result.area} m²
              </div>
            </div>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ring-1 ${
                result.anyConstraint ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
              }`}
            >
              {result.anyConstraint ? 'One or more constraints apply' : 'No constraints detected'}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(LAYER_LABELS).map(([code, label]) => {
              const layer = result.constraintReport[code as keyof typeof result.constraintReport];
              const hit = layer?.intersects === true;
              return (
                <div
                  key={code}
                  className={`rounded-lg border px-3.5 py-3 ${hit ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
                >
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                  <div className={`mt-1 text-sm font-bold ${hit ? 'text-amber-700' : 'text-slate-700'}`}>
                    {layer ? (hit ? 'Intersects' : 'Clear') : 'No data'}
                  </div>
                  {layer && layer.features.length > 0 ? (
                    <div className="mt-0.5 text-xs text-slate-500">{layer.features.length} feature(s)</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <PageHeader title="Application classes" subtitle="Published checklist, referral matrix, SLA and fee formula per class — GET /application-classes." />
      {classesQ.error ? <ErrorNote message={classesQ.error} /> : null}
      {classesQ.loading ? <Spinner label="Loading application classes…" /> : null}
      {classesQ.data && classesQ.data.items.length === 0 ? <EmptyState title="No application classes configured" /> : null}
      <div className="space-y-2">
        {(classesQ.data?.items ?? []).map((cls) => (
          <div key={cls.code} className="card overflow-hidden">
            <button
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
              onClick={() => setExpandedClass(expandedClass === cls.code ? null : cls.code)}
            >
              <div>
                <span className="text-sm font-bold text-slate-800">{cls.name}</span>
                <span className="ml-2 text-xs font-semibold text-slate-400">{cls.code}</span>
                {!cls.active ? <span className="ml-2 text-xs font-semibold text-rose-500">INACTIVE</span> : null}
              </div>
              <span className="text-xs text-slate-400">{expandedClass === cls.code ? '▲' : '▼'}</span>
            </button>
            {expandedClass === cls.code ? (
              <div className="border-t border-slate-100 px-4 py-3 text-sm">
                <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Validation SLA</dt>
                    <dd className="font-medium text-slate-800">{cls.slaWorkingDays} working days</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Public notice period</dt>
                    <dd className="font-medium text-slate-800">{cls.publicNoticeDays} days</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Referral SLA</dt>
                    <dd className="font-medium text-slate-800">{cls.referralSlaWorkingDays} working days</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Delegated decision eligible</dt>
                    <dd className="font-medium text-slate-800">{cls.delegatedEligible ? 'Yes (SENIOR_PLANNER)' : 'No — full committee'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Deemed no-objection policy</dt>
                    <dd className="font-medium text-slate-800">{cls.deemedNoObjectionPolicy ? 'Enabled' : 'Disabled'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Fee formula</dt>
                    <dd className="font-medium text-slate-800">
                      SCR {cls.feeFormula.baseFeeScr} + {cls.feeFormula.perSqmFeeScr}/m²
                    </dd>
                  </div>
                </dl>
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Required documents</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cls.docChecklist.map((doc) => (
                      <span key={doc} className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                        {doc}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
